import { useEffect, useMemo, useState } from 'react'
import { Alert, Button, Collapse, Form, Message, Space, Switch } from '@arco-design/web-react'
import JsonListInput from '../JsonListInput'
import GroupRatioEditor from './GroupRatioEditor'
import GroupEconomyTable from './GroupEconomyTable'
import CrossGroupRatioEditor from './CrossGroupRatioEditor'
import type { OptionsAPI } from '../useOptions'
import {
  createRatioRowId,
  crossRowsToNested,
  groupRowsToMaps,
  mergeGroupRows,
  parseCrossGroupRows,
  parseNumberMap,
  parseStringMap,
  validateRatioForm,
  buildGroupConfigWarnings,
  type CrossGroupRow,
  type GroupRow,
} from '../ratioSettingUtils'
import {
  JsonOptionSection,
  validateJsonObject,
  validateJsonStringArray,
  type JsonOptionFieldDef,
} from './jsonOptionSection'

const GROUP_SPECIAL_KEY = 'group_ratio_setting.group_special_usable_group'

const ADVANCED_JSON_FIELDS: JsonOptionFieldDef[] = [
  {
    key: GROUP_SPECIAL_KEY,
    label: '分组特殊可用分组（高级 JSON）',
    placeholder: '为一个 JSON 文本',
    extraText: '键为用户分组；内层键以 +:/-: 开头表示增删可用分组',
    validate: validateJsonObject,
  },
]

export default function GroupRatioJsonSection({ options }: { options: OptionsAPI }) {
  const { get, getBool, patch, save, saving, dataRevision } = options
  const [groupRows, setGroupRows] = useState<GroupRow[]>([])
  const [crossRows, setCrossRows] = useState<CrossGroupRow[]>([])
  const [autoGroupsRaw, setAutoGroupsRaw] = useState('')
  const [defaultAuto, setDefaultAuto] = useState(false)

  useEffect(() => {
    const ratioMap = parseNumberMap(get('GroupRatio'))
    const nameMap = parseStringMap(get('UserUsableGroups'))
    setGroupRows(mergeGroupRows(ratioMap, nameMap, () => createRatioRowId('group')))
    setCrossRows(parseCrossGroupRows(get('GroupGroupRatio'), () => createRatioRowId('cross')))
    setAutoGroupsRaw(get('AutoGroups', '[]'))
    setDefaultAuto(getBool('DefaultUseAutoGroup'))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataRevision])

  const warnings = useMemo(
    () =>
      buildGroupConfigWarnings({
        groupRows,
        crossRows,
        autoGroupsRaw,
        specialUsableRaw: get(GROUP_SPECIAL_KEY),
        defaultUseAutoGroup: defaultAuto,
      }),
    [autoGroupsRaw, crossRows, defaultAuto, get, groupRows]
  )

  const syncStructuredToPatch = () => {
    const { groupRatio, userUsable } = groupRowsToMaps(groupRows)
    patch({
      GroupRatio: JSON.stringify(groupRatio),
      UserUsableGroups: JSON.stringify(userUsable),
      GroupGroupRatio: JSON.stringify(crossRowsToNested(crossRows)),
      AutoGroups: autoGroupsRaw,
      DefaultUseAutoGroup: String(defaultAuto),
    })
  }

  const autoGroupKeys = useMemo(() => {
    try {
      const parsed = JSON.parse(autoGroupsRaw || '[]')
      return Array.isArray(parsed) ? parsed.map((v) => String(v).trim()).filter(Boolean) : []
    } catch {
      return []
    }
  }, [autoGroupsRaw])

  const saveStructured = async () => {
    const err = validateRatioForm({
      groupRows,
      crossRows,
      modelRatio: [],
      completionRatio: [],
      modelPrice: [],
    })
    if (err) {
      Message.error(err)
      return
    }
    const autoErr = validateJsonStringArray(autoGroupsRaw)
    if (autoErr) {
      Message.error(`AutoGroups：${autoErr}`)
      return
    }
    const { groupRatio, userUsable } = groupRowsToMaps(groupRows)
    await save(
      ['GroupRatio', 'UserUsableGroups', 'GroupGroupRatio', 'AutoGroups', 'DefaultUseAutoGroup'],
      {
        GroupRatio: JSON.stringify(groupRatio),
        UserUsableGroups: JSON.stringify(userUsable),
        GroupGroupRatio: JSON.stringify(crossRowsToNested(crossRows)),
        AutoGroups: autoGroupsRaw,
        DefaultUseAutoGroup: String(defaultAuto),
      }
    )
  }

  return (
    <div className="space-y-3">
      {warnings.map((w) => (
        <Alert key={w.key + w.message} type="warning" content={w.message} className="!mb-0" />
      ))}

      <div className="settings-toolbar settings-toolbar--sticky">
        <Space>
          <Button type="primary" loading={saving} onClick={saveStructured}>
            保存分组相关设置
          </Button>
          <Button onClick={syncStructuredToPatch}>同步草稿到表单</Button>
        </Space>
        {saving ? <span className="settings-save-status">正在保存…</span> : null}
      </div>

      <Form layout="vertical" className="settings-compact-form">
        <div className="settings-subsection mb-3">
          <Form.Item label="基础分组与展示名" className="!mb-0">
            <GroupRatioEditor
              rows={groupRows}
              autoGroupKeys={autoGroupKeys}
              onChange={(rows) => {
                setGroupRows(rows)
                const { groupRatio, userUsable } = groupRowsToMaps(rows)
                patch({
                  GroupRatio: JSON.stringify(groupRatio),
                  UserUsableGroups: JSON.stringify(userUsable),
                })
              }}
            />
          </Form.Item>
        </div>
        <div className="settings-subsection mb-3">
          <Form.Item label="分组经济视图" className="!mb-0">
            <GroupEconomyTable groupRows={groupRows} />
          </Form.Item>
        </div>
        <div className="settings-subsection mb-3">
          <Form.Item label="用户组 × 使用组 交叉倍率" className="!mb-0">
            <CrossGroupRatioEditor
              rows={crossRows}
              groupRows={groupRows}
              onChange={(rows) => {
                setCrossRows(rows)
                patch({ GroupGroupRatio: JSON.stringify(crossRowsToNested(rows)) })
              }}
            />
          </Form.Item>
        </div>
        <Form.Item
          label="Auto 分组候选顺序"
          extra="token 分组为 auto 时按此顺序尝试；JSON 字符串数组"
        >
          <JsonListInput
            value={autoGroupsRaw}
            onChange={(v) => {
              setAutoGroupsRaw(v)
              patch({ AutoGroups: v })
            }}
            placeholder="default, vip, svip"
          />
        </Form.Item>
        <Form.Item label="创建令牌默认选择 auto 分组" extra="否则留空，使用用户默认分组">
          <Switch
            checked={defaultAuto}
            onChange={(v) => {
              setDefaultAuto(v)
              patch({ DefaultUseAutoGroup: String(v) })
            }}
          />
        </Form.Item>
      </Form>

      <Collapse bordered={false} className="settings-advanced-collapse settings-meta-collapse">
        <Collapse.Item header="高级：特殊可用分组 JSON" name="special">
          <JsonOptionSection
            options={options}
            fields={ADVANCED_JSON_FIELDS}
            saveLabel="保存特殊可用分组"
          />
        </Collapse.Item>
      </Collapse>
    </div>
  )
}
